<template>
  <div
    v-if="field && application && isVisible(field, application)"
    :class="[isErrorClass(field, application), field.className]"
  >
    <label v-if="canFieldUseLabel(field, application)">
      <span class="fieldLabel">
        <span
          class="required-mark"
          aria-hidden="true"
          v-if="getRequired(field, application)"
          >*</span
        >
        <span v-if="field.label">{{ getLabel(field, application) }}</span>
        <span
          v-else-if="field.vHtmlLabel"
          v-html="getVHtmlLabel(field, application)"
        ></span>
      </span>

      <div
        v-if="isSimpleInput(field, application)"
        class="fieldContent fieldText"
      >
        <input
          :aria-required="!!getRequired(field, application)"
          :ref="fieldId"
          :type="getType(field, application) === 'EMAIL' ? 'email' : 'text'"
          :value="getValue(field)"
          :maxlength="getMaxLengthAttribute(field, application)"
          :placeholder="field.placeHolder"
          :readonly="isDisabled(field, application)"
          :aria-disabled="isDisabled(field, application)"
          @blur="setValue(section, field, $event.target.value)"
          :aria-describedby="field.apiKey"
        />
      </div>
      <div
        v-if="getType(field, application) === 'CURRENCY'"
        class="fieldContent"
      >
        <currency-input
          :aria-required="!!getRequired(field, application)"
          :ref="fieldId"          
          :value="getValue(field)"
          :maxlength="getMaxLengthAttribute(field, application)"
          :placeholder="field.placeHolder"
          :readonly="isDisabled(field, application)"
          :aria-disabled="isDisabled(field, application)"
          @blur="setValueNumber(section, field, $event.target.value)"
          :aria-describedby="field.apiKey"
        />
      </div>     
       <div
        v-if="getType(field, application) === 'PHONE'"
        class="fieldContent"
      >
        <input
          :aria-required="!!getRequired(field, application)"
          :ref="fieldId"
          :type="getType(field, application) === 'tel'"         
          :value="getValue(field)"
          :maxlength="getMaxLengthAttribute(field, application)"
          :placeholder="field.placeHolder"
          :readonly="isDisabled(field, application)"
          :aria-disabled="isDisabled(field, application)"         
          @input="enforcePhoneFormat(field, $event.target.value)"
          @blur="setValue(section, field, $event.target.value)"
          :aria-describedby="field.apiKey"
        />
      </div>
      <div
        v-if="getType(field, application) === 'NUMBER'"
        class="fieldContent"
      >
        <input
          :aria-required="!!getRequired(field, application)"
          :ref="fieldId"
          :type="getType(field, application) === 'number'"         
          :value="getValue(field)"
          :maxlength="getMaxLengthAttribute(field, application)"
          :placeholder="field.placeHolder"
          :readonly="isDisabled(field, application)"
          :aria-disabled="isDisabled(field, application)"
          @blur="setValue(section, field, $event.target.value)"
          :aria-describedby="field.apiKey"
        />
      </div>

      <div
        v-if="getType(field, application) === 'TEXTAREA'"
        class="fieldContent"
      >
        <textarea
          :aria-required="!!getRequired(field, application)"
          :rows="getRows(field, application)"
          :ref="fieldId"
          :value="getValue(field)"
          :maxlength="getMaxLengthAttribute(field, application)"
          :placeholder="field.placeHolder"
          @blur="setValue(section, field, $event.target.value)"
          :readonly="isDisabled(field, application)"
          :aria-disabled="isDisabled(field, application)"
        ></textarea>
      </div>

      <div
        v-if="getType(field, application) === 'DROPDOWN'"
        class="fieldContent fieldSelect"
      >
        <select
          :aria-required="!!getRequired(field, application)"
          :ref="fieldId"
          :value="getValue(field)"
          :class="[{ isPlaceholder: getValue(field) === '' }]"
          @change="onChange(section, field, $event.target.value)"
          :disabled="isDisabled(field, application)"
          :aria-describedby="field.apiKey"
        >
          <option
            :disabled="isDisabled(option, application)"
            v-for="(option, index) in getOptions(field, application, store)"
            :key="typeof(option) === 'object' ? option.value + index : option"
            :value="typeof(option) === 'object' ? option.value : option"
          >{{ typeof(option) === 'object' ? option.text : option }}</option>
        </select>
      </div>
    </label>

    <fieldset
      v-if="getType(field, application) === 'RADIO'"
      class="fieldContent"
    >
      <legend class="fieldLabel">
        <span
          class="required-mark"
          aria-hidden="true"
          v-if="getRequired(field, application)"
          >*</span
        >
        {{ getLabel(field, application) }}
        <span class="sr-only" v-if="getRequired(field, application)"
          >Required</span
        >
      </legend>

      <AppRadioButton
        v-for="option in getOptions(field, application)"
        :key="option.value"
        :disabled="
          isDisabled(field, application) || isDisabled(option, application)
        "
        :ref="fieldId"
        :label="option.text"
        :checked="getValue(field) === option.value"
        @change="setRadioButton(section, field, option.value, ...arguments)"
        :ariaDescribedBy="field.apiKey"
      />
    </fieldset>

    <AppCheckboxGroup
      v-if="getType(field, application) === 'CHECKBOX_GROUP'"
      :ref="fieldId"
      :field="field"
      :label="getLabel(field, application)"
      :value="getValue(field)"
      @change="setValue(section, field, ...arguments)"
      :options="getOptions(field, application)"
      :ariaDescribedBy="field.apiKey"
    />

    <AppCheckbox
      v-if="getType(field, application) === 'CHECKBOX'"
      :disabled="isDisabled(field, application)"
      :ref="fieldId"
      :label="getLabel(field, application)"
      :vHtmlLabel="getVHtmlLabel(field, application)"
      :value="getValue(field)"
      @change="setValue(section, field, ...arguments)"
      :trueValue="field.trueValue"
      :falseValue="field.falseValue"
      :required="getRequired(field, application)"
    />

    <div v-if="getType(field, application) === 'DATE'">
      <span class="fieldLabel">
        <span
          class="required-mark"
          aria-hidden="true"
          v-if="getRequired(field, application)"
          >*</span
        >
        <span v-if="field.label">{{ getLabel(field, application) }}</span>
        <span
          v-else-if="field.vHtmlLabel"
          v-html="getVHtmlLabel(field, application)"
        ></span>
      </span>

      <AppDatePicker
        :ref="fieldId"
        :disabled="isDisabled(field, application)"
        :disabledFields="getDisabledFields(field, application)"
        :label="getLabel(field, application)"
        :value="getValue(field)"
        :selectMonth="field.selectMonth"
        :futureYears="field.futureYears"
        :pastYears="field.pastYears"
        :startYearOverride="startYear(field, application)"
        :endYearOverride="endYear(field, application)"
        :ascendingYears="field.ascendingYears"
        @change="setDate(section, field, ...arguments)"
        :required="getRequired(field, application)"
      />
    </div>
    

    <div v-if="getType(field, application) === 'DOCUMENTS'">
      <FileUpload
        :value="getValue(field)"
        :disabled="isDisabled(field, application)"
        :required="getRequired(field, application)"
        :field="field"
        :section="section"
        @change="setValue(section, field, ...arguments)"
      />
    </div>   

    <div v-if="getType(field, application) === 'LINKTO'">
      <button
        class="button-link-to primary"
        type="button"
        v-scroll-to="field.value"
      >
        {{ field.label }}
      </button>
    </div>

    <div v-if="getType(field, application) === 'BUTTON'">
      <button
        class="button-link-to primary"
        type="button"
        @click="onButtonClick"
      >
        {{ field.label }}
      </button>
    </div>

    <div v-if="getType(field, application) === 'CUSTOM'">
      <ApplicationFieldCustom :field="field" />
    </div>

    <div v-if="getAlert().type" class="alertRow">
      <font-awesome-icon icon="exclamation-circle" />
      <span class="alertMessage">{{ getAlert().message }}</span>
    </div>

    <div v-if="field.additionalInfo">
      <button
        class="additionalInfoButton"
        type="button"
        @click="openAdditionalInfo(field)"
      >
        {{ field.additionalInfo.linkText }}
      </button>
    </div>

    <div
      v-if="canFieldUseDescription(field, application)"
      :id="field.apiKey"
    >
      <div class="smallMessage" v-if="field.description">{{ getDescription(field, application) }}</div>
      <div
        class="smallMessage"
        v-if="field.vHtmlDescription"
        v-html="getVHtmlDescription(field, application)"
      ></div>
      <ul class="smallMessage" v-if="field.descriptionList">
        <li
          v-for="item in field.descriptionList"
          :key="item"
          v-html="item"
        ></li>
      </ul>
      <ul class="smallMessage" v-if="field.vHtmlDescriptionList">
        <li
          v-for="item in getVHtmlDescriptionList(field, application)"
          :key="item"
          v-html="item"
        ></li>
      </ul>
    </div>

    <div v-if="field.infoPanel">

      <ApplicationInfoPanel>
        <template v-slot:icon>
          <img :src="require(`../../../src/assets/${field.infoPanel.icon}`)" alt="information icon" style="width:80px;"/>
        </template>
        <template v-slot:body>
          <h2>{{ field.infoPanel.title(application) }}</h2>
          <p>
            {{field.infoPanel.textBeforeButton}}
            <button class="no-styling open-additional-info" @click="openAdditionalInfoFromPanel(field)">here</button>
            {{field.infoPanel.textAfterButton}}

          </p>
        </template>
      </ApplicationInfoPanel>
    </div>
  </div>
</template>

<script>
import _get from 'lodash/get'

import AppCheckbox from '@/components/app/AppCheckbox.vue'
import AppCheckboxGroup from '@/components/app/AppCheckboxGroup.vue'
import AppDatePicker from '@/components/app/AppDatePicker.vue'
import AppRadioButton from '@/components/app/AppRadioButton.vue'
import ApplicationFieldCustom from '@/components/application/ApplicationFieldCustom'
import ApplicationInfoPanel from '@/components/application/ApplicationInfoPanel'

import FileUpload from '@/components/app/FileUpload'
import fieldsMixin from '@/components/mixins/fieldsMixin'
import { FIELD_TYPE } from '@/constants'

export default {
  name: 'ApplicationField',
  components: {
    AppCheckbox,
    AppCheckboxGroup,
    AppDatePicker,
    AppRadioButton,    
    ApplicationFieldCustom,
    FileUpload,
    ApplicationInfoPanel
  },
  props: ['field', 'section'],
  data() {
    return {
      checkedValue: []
    }
  },
  computed: {
    fieldId() {
      return this.section.id + '>' + this.field.apiKey
    },
    application() {
      return this.$store.state.application || {}
    },
    store() {
      return this.$store
    }
  },
  mixins: [fieldsMixin],
  methods: {
    isErrorClass(field, application) {
      // Three possible responses: error field, error child fields, no error
      if (this.getAlert().type) {
        // Address only display child field errors
        return (
          'ApplicationField ' +
          (this.getType(field, application) === 'ADDRESS'
            ? 'hasAlertChild'
            : 'hasAlert')
        )
      } else {
        return 'ApplicationField'
      }
    },
    canFieldUseDescription(field, application) {
      const type = this.getType(field, application)
      return (
        type !== FIELD_TYPE.DOCUMENTS &&
        (field.description ||
          field.descriptionList ||
          field.vHtmlDescription ||
          field.vHtmlDescriptionList)
      )
    },
    canFieldUseLabel(field, application) {
      const type = this.getType(field, application)
      // Only fields with a single input should be wrapped in a label tag
      return (
        type !== 'RADIO' &&        
        type !== 'CHECKBOX_GROUP' &&
        type !== 'CHECKBOX' &&
        type !== 'LINKTO' &&
        type !== 'BUTTON' &&
        type !== 'DATE' &&
        type !== 'ADDRESS' &&
        type !== 'LOOKUP' &&
        type !== 'DOCUMENTS'
      )
    },
    isSimpleInput(field, application) {
      const type = this.getType(field, application)
      return (
        type !== 'TEXTAREA' &&
        type !== 'RADIO' &&
        type !== 'CURRENCY' &&
        type !== 'NUMBER' &&
        type !== 'CHECKBOX_GROUP' &&
        type !== 'CHECKBOX' &&
        type !== 'DATE' &&
        type !== 'LINKTO' &&
        type !== 'BUTTON' &&
        type !== 'DROPDOWN' &&
        type !== 'ADDRESS' &&
        type !== 'LOOKUP' &&
        type !== 'CUSTOM' &&
        type !== 'DOCUMENTS' &&      
        type !== 'PHONE'
      )
    },
    getRows(field, application) {
      const type = this.getType(field, application)
      if (type === 'TEXTAREA' && !field.maxRows) {
        return 5
      } else {
        return field.maxRows
      }
    },
    getMaxLengthAttribute(field, application) {
      const type = this.getType(field, application)

      // TEXTAREA is deliberately ignored here, as we want to explicity show
      // the error message to users. By adding maxlength="2048" to our inputs
      // we prevent the error message from ever being displayed. The actual
      // length checking is done in `store/buildAlerts.js`
      if (field.maxLength) {
        return field.maxLength
      } else if (type === 'POSTCODE') {
        return '4'
      } else if (type === 'MONTH_YEAR') {
        return '7'
      } else if (type === 'PHONE') {
        return '14'
      }
    },

     enforcePhoneFormat(field, val) {      
      var replacedInput = val.replace(/\D/g, '').match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
      field.value = !replacedInput[2] ? replacedInput[1] : '(' + replacedInput[1] + ') ' + replacedInput[2] + (replacedInput[3] ? '-' + replacedInput[3] : '');      
      this.$store.dispatch('set', [`application.${field.apiKey}`, field.value])
    },

    getVHtmlDescriptionList(field, application) {
      if (typeof field.vHtmlDescriptionList === 'function') {
        return field.vHtmlDescriptionList(application, this.$store)
      } else if (field.vHtmlDescriptionList) {
        return field.vHtmlDescriptionList
      }
    },

    getType(field, application) {
      if (typeof field.type === 'function') {
        return field.type(application)
      } else if (field.type) {
        return field.type
      }
    },

    onFieldSet(field, val) {
      if (typeof field.onFieldSet === 'function') {
        try {
          field.onFieldSet(val, this.application, this.$store)
        } catch (ex) {
          // eslint-disable-next-line no-console
          console.log(`${field.apiKey} - ERROR IN onFieldSet() CONFIGURATION`)
        }
      }
    },

    setValue(section, field, val, force) {
      const oVal = _get(this, `$store.state.application.${field.apiKey}`)
      if (oVal !== val || force) {
       
        this.$store.dispatch('set', [`application.${field.apiKey}`, val])

        if (field.onFieldSet && !field.onFieldSetAfterAlerts) {
          this.onFieldSet(field, val)
        }
        this.$store.dispatch('addAlert', { section, field })

        this.$store.dispatch('buildCounters')
        this.$store.dispatch('buildAlerts')

        if (field.onFieldSet && field.onFieldSetAfterAlerts) {
          this.onFieldSet(field, val)
        }

        this.$store
          .dispatch('saveApplicationSection', { section, type: '' })
          .catch(() => {
            this.$store.dispatch('set', ['saveStatus', null])
            this.$store.dispatch('set', [
              'error',
              new Error('Error when auto-saving: Could not reach server')
            ])

            this.$router.history.push('/error')
          })
      }
    },
    setValueNumber(section, field, val, force) {
      const oVal = _get(this, `$store.state.application.${field.apiKey}`)
      if (oVal !== val || force) {
       
        this.$store.dispatch('set', [`application.${field.apiKey}`, parseFloat(val)])

        if (field.onFieldSet && !field.onFieldSetAfterAlerts) {
          this.onFieldSet(field, parseFloat(val))
        }
        this.$store.dispatch('addAlert', { section, field })

        this.$store.dispatch('buildCounters')
        this.$store.dispatch('buildAlerts')

        if (field.onFieldSet && field.onFieldSetAfterAlerts) {
          this.onFieldSet(field, parseFloat(val))
        }

        this.$store
          .dispatch('saveApplicationSection', { section, type: '' })
          .catch(() => {
            this.$store.dispatch('set', ['saveStatus', null])
            this.$store.dispatch('set', [
              'error',
              new Error('Error when auto-saving: Could not reach server')
            ])

            this.$router.history.push('/error')
          })
      }
    },
    onChange(section, field, val) {
      if (typeof field.onChange === 'function') {
        try {
          field.onChange(val, this.$store)
        } catch (ex) {
          // eslint-disable-next-line no-console
          console.log(`${field.apiKey} - ERROR IN onChange() CONFIGURATION`)
        }
      }
      this.setValue(section, field, val)
    },
    setDate(section, field, val) {
      this.setValue(section, field, val)
    },
    setRadioButton(section, field, val, checked) {
      this.setValue(section, field, checked ? val : '')
    },
    getAlert() {
      var alerts = [...this.$store.state.alerts]
      var alert = alerts.find(
        a =>
          a.section.id === this.section.id &&
          a.field.apiKey === this.field.apiKey
      )
      return alert || {}
    },
    setFocus() {
      var input = this.$refs[this.fieldId]
      if (!input.tagName) {
        // TODO: Refactor to make less brittle
        if (input.length) {
          // Find selected radio button...
          var selectedRadioButton = input.find(el => el.checked)
          input = selectedRadioButton.$el.control || input[0].$el.control
        } else {
          // Find first select tag in date picker
          input = input.$el.firstChild.firstChild
        }
      }
      input.focus()
    },
    openAdditionalInfo(field) {
      this.$store.dispatch('openModal', field.additionalInfo)
    },
    openAdditionalInfoFromPanel(field) {
      this.$store.dispatch('openModal', field.infoPanel)
    },
    onButtonClick(event) {
      this.field.onClick.call(this, event)
    }
  }
}
</script>

<style lang="scss" scoped>
.ApplicationField {
  display: inline-block;
  margin-bottom: 3.5rem;
  padding: 0 $content-padding;
  width: 100%;
  vertical-align: top;

  label {
    display: block;
  }

  input,
  textarea {
    &:read-only {
      background: $color-grey-10;
      color: $color-primary;
    }
  }

  input,
  textarea,
  select {
    width: 100%;

    &.isPlaceholder {
      color: $color-text-light;
    }

    &:disabled {
      background: $color-grey-10;
      color: $color-primary;
    }
  }

  .fieldContent {
    padding-top: 0.25rem;
    font-size: $text-small;
  }

  ::v-deep .fieldText,
  ::v-deep .fieldSelect {
    width: 100%;

    @include desktop {
      width: 50%;
    }
  }

  button.button-link-to {
    margin: 0;
    padding: 0;
    min-width: 7rem;
    border: 0;
    background: transparent !important;
    color: $color-primary;
    font-size: 1.6rem;
  }

  ::v-deep .fieldLabel {
    display: block;
    margin: 0 0 0.6rem 0;
    color: $color-text-body;
    font-weight: normal;
    font-size: $text-h5;

    > span.required-mark {
      color: $color-red;
    }

    .required-mark {
      float: left;
    }
  }

  &.fieldInline {
    display: inline-block;
    width: auto;
  }

  &.hasAlert,
  &.hasAlertChild ::v-deep .error-style {
    padding-left: 0.5rem;
    border-left: 0.5em solid $color-red;
  }

  .alertRow {
    display: flex;
    padding-top: 0.5rem;

    svg {
      position: relative;
      top: -0.5rem;
      height: 3em;
      color: $color-red;
    }
  }

  .alertMessage {
    display: inline-block;
    padding-left: 0.25rem;
    color: $color-red;
    font-size: $text-small;
  }

  .icon {
    margin-top: 0.2em;
    margin-right: 0.6em;
    color: $color-red;
    font-size: 2.5rem;
  }

  .smallMessage,
  ::v-deep .smallMessage {
    display: block;
    padding-top: 0.8rem;
    font-size: $text-small;

    ::v-deep > p {
      font-size: $text-small;
    }

    ::v-deep > p:last-child {
      margin-bottom: 0;
    }
  }

  .additionalInfoButton {
    margin: 0;
    padding: 0.8rem 0 0;
    border: none;
    background: transparent;
    color: $color-primary;
    font-weight: 700;
    font-size: 1.4rem;
    text-decoration: underline;
  }

  ::v-deep .review-only-label {
    @include sr-only;
  }
}

.boxWhite {
  background: $color-white;
  padding: 1rem;
}

::v-deep .boxGrey {
  background: $color-grey-10;
  padding: 1rem;
  border-radius: 0.2rem;
  color: $color-primary;
  margin-top: 1rem;
  font-size: 1.8rem;
  font-weight: 500;
}
</style>
